import { DAY_MS, WEEK_MS } from "./freshness.js";
import { rollingWindowStart } from "./weekWindow.js";
import { decayedHardSetRate } from "./muscleState.js";

// Form badge — a qualitative Ready / Caution / Rest flag for the Home hero,
// derived ONLY from Metria's own logged data (no sensors, no HRV). It is a
// training-load heuristic, NOT an injury or medical prediction — the UI must
// frame it as a gentle flag. Lifted from apps/mobile into shared with the Home
// hero surface so mobile + the web-rnw console compute one readiness flag.
//
// ⭐⭐ RIFATTO il 2026-09-15 notte (`HM-3`) sul modello di `measurement-contract.md §8ter`.
// ⛔ Il codice che c'era prima l'ho scritto io, e Antenore l'aveva approvato con
// le informazioni che gli avevo dato io. La ricognizione della letteratura
// (`next/recovery-window-research.md`) l'ha poi demolito su tre punti:
//
//  1. 🪤 Era un ACWR **accoppiato**: `vol7 / (vol28 / 4)`, cioè la finestra acuta
//     stava DENTRO la cronica. Correlazione spuria per costruzione, prima di
//     qualunque fisiologia (Lolli et al., BJSM 2019;53(15):921-922).
//  2. 🪤 Le soglie erano `1.3` e `1.5`, i numeri di Gabbett/Blanch, **senza
//     provenienza dichiarata** — nati su sport di squadra con esito INFORTUNIO.
//     Sulla pesistica non esiste **nessuno** studio, e la meta-analisi più
//     recente dice di non usarlo come segnale a sé (PMID 42662491: effetto
//     piccolo, g = 0.35, con I² = 95.8%).
//  3. 🪤 `RECOVERED_DAYS = 2` era esattamente l'euristica «due giorni e sei
//     riposato» che la ricognizione ha falsificato: i marcatori del recupero
//     divergono di giorni fra loro, e a volume pari la frequenza non cambia
//     l'ipertrofia (PMID 30558493) ⇒ «recuperato» non è un prerequisito per
//     crescere, e non è uno stato che questa app possa dichiarare.
//
// ⇒ Cosa c'è adesso, da §8ter: lo stato è una **quantità continua che DECADE**,
// costruita sulle **serie dure** (`hardSetCount`, che è GIÀ filtrato per
// prossimità al cedimento in `sessionResult.ts` via `effectiveRir`), con due
// scale di tempo diverse. ⛔ Nessuna soglia in ore o giorni per il recupero,
// ⛔ nessun binario riposato/non-riposato, ⛔ e la parola «recovered» non
// compare più né qui né in una stringa.

export type FormZone = "ready" | "caution" | "rest";

// ⛔ `wellRecovered` è stato TOLTO, non rinominato: asseriva una cosa falsa
// («ben riposato dall'ultima sessione ⇒ spingi») su una soglia di due giorni
// che non esiste. Uno stato «ready» senza niente di notevole è `balanced`.
export type FormReason =
  | "noData"
  | "balanced"
  | "freshStart"
  | "shortRest"
  | "highFrequency"
  | "loadSpike"
  | "longGap";

export type FormBadge = { zone: FormZone; reason: FormReason };

// ⭐ `hardSets` è l'asse dello stato (§8ter): un CONTEGGIO di serie dure, non
// un tonnellaggio. ⛔ Le due grandezze non stanno sullo stesso asse (§16.3), e
// il modello prima girava sul tonnellaggio per sola inerzia di come era nato.
// `volumeKg` resta perché la card delle statistiche di Home lo mostra.
export type FormSession = { startedAtMs: number; volumeKg: number; hardSets: number };

export type HomeWeekly = { sessionsLast7d: number; volumeKgLast7d: number };

/** Count + volume of the last 7 days for the Home stats card: the window of
 *  computeWeeklyTargets, on the same card (#463). */
export function computeHomeWeekly(
  sessions: FormSession[],
  nowMs: number,
): HomeWeekly {
  const windowStart = rollingWindowStart(nowMs, 7);
  let sessionsLast7d = 0;
  let volumeKgLast7d = 0;
  for (const s of sessions) {
    if (s.startedAtMs < windowStart || s.startedAtMs > nowMs) continue;
    sessionsLast7d++;
    volumeKgLast7d += s.volumeKg;
  }
  return { sessionsLast7d, volumeKgLast7d };
}

// ⛔⛔ LE DUE EMIVITE SONO IPOTESI NOSTRE, e vanno lette come tali.
// §8ter lo dice a chiare lettere: **non esiste in letteratura un numero da
// copiare**. Quello che la letteratura sostiene è la FORMA — un disturbo che
// decade, più marcato quanto più la serie è vicina al cedimento (relazione
// lineare, PMID 36752989) — non la costante di tempo.
//
// ⚠️ Chi le cambia non sta correggendo un errore: sta tarando. Il posto giusto
// per tararle sono i suoi dati, ed è il residuo dichiarato di `PG-20`.
// ⛔ Non sono soglie: sono i pesi di una media. Nessun evento «esce» da una
// finestra, e nessun contatore si azzera.
//
// 🪤⭐ **L'emivita veloce DEVE coprire almeno un ciclo di allenamento.** La prima
// stesura usava 2 giorni — la scala della fatica — ed era sbagliata per QUESTO
// mestiere: misurata su ritmi perfettamente costanti ma a grappoli (3× o 5× a
// settimana) dava un eccesso apparente del **56-61%**, perché leggeva **in che
// punto della settimana ti trovi**, non se stai facendo più del solito.
// ⇒ È lo stesso difetto di §8bis, in un'altra forma: *misurare che giorno è,
// non l'atleta*. A 7 giorni la fase si cancella (escursione misurata sul giorno
// della settimana: **±0.11 al massimo**).
// ⚠️ Questo NON è il segnale di fatica per-muscolo di §8ter.1, che vive su una
// scala più veloce: qui il mestiere è «il carico sta cambiando?», ed è un'altra
// domanda. Due mestieri, due scale — di nuovo.
const ACUTE_HALF_LIFE_DAYS = 7;
const CHRONIC_HALF_LIFE_DAYS = 28;

// Quante sessioni servono perché il livello cronico voglia dire qualcosa.
// ⚠️ NOSTRA e **non dedotta da una misura**, a differenza delle due soglie di
// eccesso più sotto: è un minimo di prudenza scelto a tavolino, e resta da
// tarare come le emivite (`PG-20`). ⛔ Dichiarato invece che lasciato muto.
const MIN_BASELINE_SESSIONS = 4;

// ⚠️ Conteggi di sessioni, non affermazioni sul recupero: «hai allenato N volte
// in 7 giorni» è un fatto. ⛔ Anche questi sono NOSTRI e senza provenienza
// esterna — erano già qui prima, e restano da tarare come le emivite.
const VERY_HIGH_FREQ_7D = 6;
const HIGH_FREQ_7D = 5;
const BUSY_FREQ_7D = 4;

// Assenza prolungata: detraining / «sei tornato?». ⛔ NON è un'affermazione sul
// recupero — è un fatto sul tempo passato senza allenarsi.
const LONG_GAP_DAYS = 14;

// «Hai appena finito»: mezza giornata dall'ultima sessione. ⛔ Anche questo è un
// fatto sul tempo, non un giudizio sul recupero — la card lo dice **insieme** a
// una settimana già piena, mai da solo. ⚠️ NOSTRA, non dedotta.
const SHORT_REST_DAYS = 0.5;

// Di quanto il ritmo recente deve superare il PROPRIO ritmo di fondo perché la
// card lo nomini. ⛔⛔ Sono frazioni del suo stesso cronico, mai le soglie
// pubblicate: `1.3`/`1.5` erano di Gabbett e non tornano, in nessuna forma.
// ⚠️ Resta vero che un RAPPORTO fra due carichi porta con sé i problemi che
// Impellizzeri et al. (PMID 32502973) elencano — categorizzazione arbitraria e
// statistica dei rapporti — e l'unico che qui è SPARITO è l'annidamento: acuto
// e cronico sono due pesature diverse della STESSA storia intera, nessuna delle
// due è una sotto-finestra dell'altra. Gli altri restano, dichiarati.
//
// ⭐ **Dedotte da una misura, non dichiarate.** Il pavimento di rumore è stato
// misurato su ritmi costanti realistici (2×, 3×, 5× a settimana, ogni giorno della
// settimana): l'eccesso oscilla fra **-0.11 e +0.11** senza che il carico cambi.
// Le due soglie stanno oltre il DOPPIO di quel pavimento. Per confronto misurato
// sugli stessi dati: un picco vero dà **+1.30**, uno scarico **-0.26**.
const EXCESS_SHARP = 0.5; // +50% sul proprio ritmo di fondo
const EXCESS_ELEVATED = 0.25;

/**
 * Reduce recent logged sessions to a single readiness flag.
 * @param sessions per-session {startedAtMs, volumeKg, hardSets}; order-independent.
 * @param nowMs    current time in ms (injected so the result is testable).
 */
export function computeFormBadge(
  sessions: ReadonlyArray<FormSession>,
  nowMs: number,
): FormBadge {
  const recent = sessions
    .filter((s) => s.startedAtMs <= nowMs)
    .sort((a, b) => b.startedAtMs - a.startedAtMs);
  if (recent.length === 0) return { zone: "ready", reason: "noData" };

  const last = recent[0]!;
  const daysSinceLast = (nowMs - last.startedAtMs) / DAY_MS;
  const count7 = recent.filter((s) => nowMs - s.startedAtMs < WEEK_MS).length;

  // ⭐ Le due pesature girano sulla storia INTERA, non su due finestre annidate:
  // è così che l'accoppiamento aritmetico dell'ACWR sparisce.
  const acute = decayedHardSetRate(recent, nowMs, ACUTE_HALF_LIFE_DAYS);
  const chronic = decayedHardSetRate(recent, nowMs, CHRONIC_HALF_LIFE_DAYS);
  // Di quanto il ritmo recente eccede il proprio ritmo di fondo. Null finché il
  // fondo non vuol dire niente: ⛔ un eccesso calcolato su due sessioni è rumore.
  const excess =
    recent.length >= MIN_BASELINE_SESSIONS && chronic > 0 ? acute / chronic - 1 : null;

  // Rest — un chiaro «rallenta».
  if (count7 >= VERY_HIGH_FREQ_7D) return { zone: "rest", reason: "highFrequency" };
  if (excess !== null && excess >= EXCESS_SHARP && count7 >= BUSY_FREQ_7D)
    return { zone: "rest", reason: "loadSpike" };

  // Caution — al limite: o sta spingendo, o è un'assenza insolita.
  if (daysSinceLast >= LONG_GAP_DAYS) return { zone: "caution", reason: "longGap" };
  if (excess !== null && excess >= EXCESS_ELEVATED)
    return { zone: "caution", reason: "loadSpike" };
  if (count7 >= HIGH_FREQ_7D) return { zone: "caution", reason: "highFrequency" };
  if (daysSinceLast < SHORT_REST_DAYS && count7 >= BUSY_FREQ_7D)
    return { zone: "caution", reason: "shortRest" };

  // Ready. ⛔ Qui NON si dice «ben riposato»: la card non sa se lo è, e la
  // letteratura dice che non è nemmeno la domanda giusta (§8ter.1).
  if (excess === null) return { zone: "ready", reason: "freshStart" };
  return { zone: "ready", reason: "balanced" };
}
